import { useEffect, useRef, type RefObject } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificacoesApi, type Notificacao, type SeveridadeNotificacao } from '@/lib/api/core';
import { useAuthStore } from '@/lib/stores/authStore';

// Cor do ponto de status de cada item (proto:555 usa `var(--${x.k==='acc'?'accent':x.k})`,
// full-tone, nao a variante `-soft`).
const COR: Record<SeveridadeNotificacao, string> = {
  critica: 'var(--crit)',
  atencao: 'var(--warn)',
  positiva: 'var(--good)',
  informativa: 'var(--accent)',
};

export function PainelNotificacoes({
  aberto,
  aoFechar,
  gatilhoRef,
}: {
  aberto: boolean;
  aoFechar: () => void;
  /**
   * Ref do botão-sino que abre este painel (TopBar.tsx). Compartilhado para
   * que o listener de "clique fora" saiba distinguir o botão do resto do
   * mundo: sem essa distinção, clicar no sino com o painel aberto dispara,
   * na mesma interação, um `mousedown` (fecha, alvo fora de `painelRef`) e
   * depois um `click` no sino (`setNotifAberto(v => !v)`, que reabre — o
   * `!false` de um estado já fechado). O sino nunca conseguia fechar o
   * próprio painel.
   */
  gatilhoRef: RefObject<HTMLElement>;
}) {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const painelRef = useRef<HTMLDivElement>(null);

  const { data: notificacoes = [] } = useQuery({
    queryKey: ['notificacoes', false],
    queryFn: () => notificacoesApi.list(token),
    enabled: !!token && aberto,
    staleTime: 60_000,
  });

  const marcar = useMutation({
    mutationFn: ({ id, lida }: { id: string; lida: boolean }) =>
      notificacoesApi.marcarLida(id, lida, token),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notificacoes'] }),
  });

  // Fecha em clique fora e em Escape (proto:555-556 fecham via toggle/closeNotif,
  // aqui replicado com listeners no document em vez de IDs globais).
  useEffect(() => {
    if (!aberto) return;

    const aoClicarFora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (gatilhoRef.current && gatilhoRef.current.contains(alvo)) return;
      if (painelRef.current && !painelRef.current.contains(alvo)) aoFechar();
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };

    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto, aoFechar]);

  if (!aberto) return null;

  // proto:555 — `NOTIFS.filter(x=>!x.lido).slice(0,6)`.
  const naoLidas = notificacoes.filter((n) => !n.lida).slice(0, 6);

  const abrir = (n: Notificacao) => {
    // `n` vem de `naoLidas`, já filtrada por `!n.lida` — sempre marca.
    marcar.mutate({ id: n.id, lida: true });
    aoFechar();
    if (n.link) navigate(n.link);
  };

  return (
    <div className="notif open" ref={painelRef} role="region" aria-label="Notificações">
      {naoLidas.map((n) => (
        <div
          key={n.id}
          className="it"
          role="button"
          tabIndex={0}
          onClick={() => abrir(n)}
          // Padrão de menu com setas foi deliberadamente descartado aqui —
          // só Enter/Espaço, que é o mínimo para o item ser operável por
          // teclado (hoje era a única forma de abrir uma notificação a
          // partir da topbar que ficava inalcançável sem mouse).
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              abrir(n);
            }
          }}
        >
          <i style={{ background: COR[n.severidade] }} />
          <div>
            <b>{n.titulo}</b>
            <br />
            <span style={{ color: 'var(--ink3)', fontSize: 12 }}>{n.descricao || n.tipo}</span>
          </div>
        </div>
      ))}
      {naoLidas.length === 0 && (
        <div className="it" style={{ cursor: 'default' }}>
          Nenhuma notificação não lida.
        </div>
      )}
      <Link
        to="/notificacoes"
        className="it"
        style={{ justifyContent: 'center', color: 'var(--accent)', textDecoration: 'none' }}
        onClick={aoFechar}
      >
        Ver todas as notificações →
      </Link>
    </div>
  );
}
